/**
 * TCVAllocationCard — v3.12.4
 *
 * Renders the derived TCV split between the Prime and each Teaming
 * Partner using `pursuit.tcv_allocation` from the backend.
 *
 * Display:
 *   - Headline: "TCV $5.0M — split across 1 Prime + 2 Teaming Partners"
 *   - Stacked horizontal bar (Prime navy on the left, partners colored
 *     by socio-economic status / entity type on the right).
 *   - Per-row breakdown with abbreviated $ amounts and % chips.
 *   - Inline alerts for: missing TCV, workshare overflow (>100%), and
 *     unallocated workshare (<100%).
 *
 * Pure presentational — does NO fetching of its own. Drop in wherever
 * you have the pursuit object: Opportunity Intake (live tweaking),
 * pursuit detail headers, kanban hover popups, etc.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Briefcase, AlertCircle, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

const PARTNER_TONES = [
  'bg-amber-500',
  'bg-teal-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-sky-500',
  'bg-lime-500',
];

const formatCurrency = (n) => {
  if (n == null || isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

export default function TCVAllocationCard({ allocation, compact = false }) {
  if (!allocation) return null;
  const { tcv, prime, partners = [], total_partner_workshare, unallocated_workshare, warning } = allocation;
  const partnerCount = partners.length;

  // Build the stacked bar segments. Prime is always navy on the left,
  // partners cycle through PARTNER_TONES (with a stable color per index
  // so the legend matches the bar). Unallocated is rendered as a dashed
  // grey segment so the gap is obvious.
  const barSegments = [
    { key: 'prime', label: prime.name, ws: prime.workshare, tone: 'bg-slate-900' },
    ...partners.map((p, i) => ({
      key: `partner-${i}`,
      label: p.partner_abbr || p.legal_name,
      ws: p.workshare,
      tone: PARTNER_TONES[i % PARTNER_TONES.length],
    })),
  ].filter((s) => s.ws > 0);
  const showUnallocated = unallocated_workshare > 0;

  const warningText = {
    tcv_missing: 'TCV not set yet — enter Estimated Value to see contract values',
    workshare_overflow: 'Partner workshare exceeds 100% — Prime allocation is 0',
    workshare_underflow: `${100 - (total_partner_workshare + prime.workshare)}% workshare unallocated`,
  }[warning];

  return (
    <Card
      className="border-slate-200 bg-white"
      data-testid="tcv-allocation-card"
    >
      <CardHeader className={cn('pb-3', compact && 'py-3')}>
        <CardTitle
          className="text-base font-semibold text-slate-900 flex items-center justify-between gap-2"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          <span className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-slate-500" />
            TCV Allocation
          </span>
          {tcv != null && (
            <span className="text-sm font-medium text-slate-600 tabular-nums" data-testid="tcv-headline">
              {formatCurrency(tcv)}
            </span>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
          <Users className="w-3 h-3" />
          1 Prime + {partnerCount} Teaming Partner{partnerCount === 1 ? '' : 's'}
        </p>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        {/* Stacked horizontal bar */}
        <div className="flex h-3 rounded-md overflow-hidden border border-slate-200">
          {barSegments.map((s) => (
            <div
              key={s.key}
              className={cn(s.tone, 'transition-all')}
              style={{ width: `${s.ws}%` }}
              title={`${s.label} · ${s.ws}%`}
            />
          ))}
          {showUnallocated && (
            <div
              className="bg-slate-100 border-l border-dashed border-slate-300"
              style={{ width: `${unallocated_workshare}%` }}
              title={`Unallocated · ${unallocated_workshare}%`}
            />
          )}
        </div>

        {/* Per-row breakdown */}
        <div className="space-y-1.5">
          {/* Prime row */}
          <AllocationRow
            tone="bg-slate-900"
            label={prime.name}
            sublabel="Prime"
            ws={prime.workshare}
            value={prime.value}
            tcvMissing={tcv == null}
            isPrime
          />
          {/* Partner rows */}
          {partners.map((p, i) => (
            <AllocationRow
              key={p.partner_abbr || `p-${i}`}
              tone={PARTNER_TONES[i % PARTNER_TONES.length]}
              label={p.partner_abbr || p.legal_name}
              sublabel={[
                p.role,
                p.is_small_business ? 'SB' : null,
                p.socio_economic_status,
              ].filter(Boolean).join(' · ')}
              ws={p.workshare}
              value={p.value}
              tcvMissing={tcv == null}
            />
          ))}
          {showUnallocated && (
            <div className="flex items-center gap-2 text-xs text-slate-500 italic px-1 pt-1 border-t border-slate-100">
              <span className="w-2 h-2 rounded-sm bg-slate-200 border border-dashed border-slate-400 flex-shrink-0" />
              <span className="flex-1">Unallocated</span>
              <span className="tabular-nums">{unallocated_workshare}%</span>
              <span className="tabular-nums w-20 text-right">
                {tcv != null ? formatCurrency(allocation.unallocated_value) : '—'}
              </span>
            </div>
          )}
        </div>

        {warningText && (
          <div
            className={cn(
              'flex items-start gap-2 px-2.5 py-2 rounded-md border text-xs',
              warning === 'workshare_overflow'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-amber-50 border-amber-200 text-amber-800',
            )}
            data-testid="tcv-allocation-warning"
          >
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{warningText}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AllocationRow({ tone, label, sublabel, ws, value, tcvMissing, isPrime }) {
  return (
    <div
      className="flex items-center gap-2 text-sm py-1"
      data-testid={isPrime ? 'tcv-row-prime' : `tcv-row-partner-${label}`}
    >
      <span className={cn('w-2 h-2 rounded-sm flex-shrink-0', tone)} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{label}</p>
        {sublabel && <p className="text-[11px] text-slate-500 truncate">{sublabel}</p>}
      </div>
      <Badge
        variant="outline"
        className="text-[10px] tabular-nums w-12 justify-center flex-shrink-0"
      >
        {ws}%
      </Badge>
      <span
        className={cn(
          'tabular-nums w-20 text-right text-sm',
          isPrime ? 'font-semibold text-slate-900' : 'text-slate-700',
          tcvMissing && 'text-slate-300',
        )}
        data-testid={isPrime ? 'tcv-prime-value' : `tcv-partner-value-${label}`}
      >
        {tcvMissing ? '—' : formatCurrency(value)}
      </span>
    </div>
  );
}
